import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Alert,
  CircularProgress,
  TextField,
  Chip
} from '@mui/material';
import { AccountBalance as QuickBooksIcon } from '@mui/icons-material';
import api from '../../config/api';

const QuickBooksSelection = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [companyInfo, setCompanyInfo] = useState(null);
  const [connectorName, setConnectorName] = useState('');
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');

  // Parse URL parameters
  const urlParams = new URLSearchParams(location.search);
  const tempTokenId = urlParams.get('temp_token_id');

  useEffect(() => {
    if (!tempTokenId) {
      setError('Missing authentication token. Please restart the QuickBooks connection process.');
      setLoading(false);
      return;
    }

    fetchCompanyInfo();
  }, [tempTokenId]);

  const fetchCompanyInfo = async () => {
    try {
      const response = await api.get('/api/connectors/quickbooks/company', {
        params: { temp_token_id: tempTokenId }
      });

      if (response.data.success) {
        setCompanyInfo(response.data.company_info);
        setConnectorName(response.data.suggested_name || `QuickBooks - ${response.data.company_info.name}`);
      } else {
        setError(response.data.message || 'Failed to fetch QuickBooks company information');
      }
    } catch (err) {
      console.error('Error fetching company info:', err);
      setError(err.response?.data?.message || 'Failed to fetch QuickBooks company information');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateConnector = async () => {
    if (!connectorName.trim()) {
      setError('Please enter a name for the connector');
      return;
    }

    setCreating(true);
    setError('');

    try {
      const response = await api.post('/api/connectors/quickbooks/create', {
        temp_token_id: tempTokenId,
        connector_name: connectorName.trim()
      });

      if (response.data.success) {
        // Redirect back to connectors page with success message
        navigate('/connectors', { 
          state: { 
            message: response.data.message,
            type: 'success'
          }
        });
      } else {
        setError(response.data.message || 'Failed to create QuickBooks connector');
      }
    } catch (err) {
      console.error('Error creating connector:', err);
      setError(err.response?.data?.message || 'Failed to create QuickBooks connector');
    } finally {
      setCreating(false);
    }
  };

  const handleReconnectExisting = async () => {
    setCreating(true);
    setError('');

    try {
      const response = await api.post('/api/connectors/quickbooks/create', {
        temp_token_id: tempTokenId,
        connector_name: connectorName.trim(),
        create_new: false // Use existing connector
      });

      if (response.data.success) {
        // Redirect back to connectors page with success message
        navigate('/connectors', { 
          state: { 
            message: response.data.message,
            type: 'success'
          }
        });
      } else {
        setError(response.data.message || 'Failed to reconnect QuickBooks');
      }
    } catch (err) {
      console.error('Error reconnecting:', err);
      setError(err.response?.data?.message || 'Failed to reconnect QuickBooks');
    } finally {
      setCreating(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading QuickBooks company information...</Typography>
      </Box>
    );
  }

  if (error && !companyInfo) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button variant="outlined" onClick={() => navigate('/connectors')}>
          Back to Connectors
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 800, mx: 'auto' }}>
      <Box sx={{ mb: 4, textAlign: 'center' }}>
        <QuickBooksIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
        <Typography variant="h4" gutterBottom>
          Confirm QuickBooks Company
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
          We've successfully authenticated with QuickBooks. Please confirm the company details and connector name.
        </Typography>
        <Alert severity="info" sx={{ mb: 2, textAlign: 'left' }}>
          <Typography variant="body2" gutterBottom>
            <strong>Wrong company?</strong> QuickBooks often auto-selects the most recently accessed company.
          </Typography>
          <Typography variant="body2" component="div">
            To connect to a different company:
            <ol style={{ marginTop: '8px', marginBottom: 0 }}>
              <li>Click "Cancel" below</li>
              <li>Log out of QuickBooks completely (quickbooks.intuit.com)</li>
              <li>Log back into QuickBooks with the desired company active</li>
              <li>Try the connection process again</li>
            </ol>
          </Typography>
        </Alert>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {companyInfo && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              QuickBooks Company Details
            </Typography>
            
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Company Name
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {companyInfo.name}
              </Typography>
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Company ID (Realm ID)
              </Typography>
              <Typography variant="body1" sx={{ 
                fontFamily: 'monospace',
                backgroundColor: 'rgba(0,0,0,0.05)',
                padding: '4px 8px',
                borderRadius: '4px'
              }}>
                {companyInfo.realm_id}
              </Typography>
            </Box>

            {companyInfo.email && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Email
                </Typography>
                <Typography variant="body1">
                  {companyInfo.email}
                </Typography>
              </Box>
            )}

            {companyInfo.country && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Country
                </Typography>
                <Typography variant="body1">
                  {companyInfo.country}
                </Typography>
              </Box>
            )}

            {companyInfo.is_already_connected && (
              <Box sx={{ mt: 2 }}>
                <Chip 
                  label="Already Connected" 
                  color="info" 
                  variant="outlined"
                  size="small"
                />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  This QuickBooks company is already connected as: {companyInfo.existing_connector_name}
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Connector Settings
          </Typography>
          
          <TextField
            fullWidth
            label="Connector Name"
            value={connectorName}
            onChange={(e) => setConnectorName(e.target.value)}
            helperText="Give this connection a memorable name"
            sx={{ mb: 3 }}
          />

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              onClick={() => navigate('/connectors')}
              disabled={creating}
            >
              Cancel
            </Button>

            {companyInfo?.is_already_connected ? (
              <Button
                variant="contained"
                onClick={handleReconnectExisting}
                disabled={creating || !connectorName.trim()}
              >
                {creating ? <CircularProgress size={20} /> : 'Reconnect'}
              </Button>
            ) : (
              <Button
                variant="contained"
                onClick={handleCreateConnector}
                disabled={creating || !connectorName.trim()}
              >
                {creating ? <CircularProgress size={20} /> : 'Connect'}
              </Button>
            )}
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default QuickBooksSelection;